import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, requestJson, requestVoid } from '../../lib/util';
import { usePxtraCardEnabled } from '../../hooks/usePxtraCardEnabled';
import { CardOfferAction } from './offerAction';
import { isNotSyncedYet, retryWhileNotSynced, SYNC_RETRY_DELAYS_MS } from './retry';
import { CardOffer, NO_CARD_OFFER } from './types';

export * from './offerAction';

/** `benefitSetId` is the legacy id; the new backend resolves it. */
export const cardOfferPath = (companyId: string, benefitSetId: string) =>
  `/api/v2/companies/${companyId}/pxtra-card/benefit-sets/${benefitSetId}/offer`;

export const cardOfferKey = (companyId: string, benefitSetId: string) => [
  'cardOffer',
  companyId,
  benefitSetId
];

/** `setKnown` is false while the new backend does not know the set yet; its
 * offer is then `NO_CARD_OFFER`. */
export type CardOfferState = { offer: CardOffer; setKnown: boolean };

const SET_NOT_KNOWN_POLL_MS = 5000;
// As long as a save waits for the sync; a set still unknown after that is not
// lagging but stuck, and polling on would not change it.
const SET_NOT_KNOWN_MAX_POLLS = Math.ceil(
  SYNC_RETRY_DELAYS_MS.reduce((sum, delay) => sum + delay, 0) / SET_NOT_KNOWN_POLL_MS
);

export const useCardOffer = (
  companyId: string,
  benefitSetId: string | undefined,
  options: { enabled?: boolean } = {}
) => {
  const { isCardEnabled } = usePxtraCardEnabled();

  return useQuery<CardOfferState>({
    queryKey: cardOfferKey(companyId, benefitSetId ?? ''),
    queryFn: async () => {
      try {
        const offer = await requestJson<CardOffer>({
          path: cardOfferPath(companyId, benefitSetId ?? '')
        });
        return { offer, setKnown: true };
      } catch (error) {
        // Backend Sync has not delivered the set yet — and then it has no
        // offer either.
        if (error instanceof ApiError && isNotSyncedYet(error)) {
          return { offer: NO_CARD_OFFER, setKnown: false };
        }
        throw error;
      }
    },
    enabled: isCardEnabled && !!companyId && !!benefitSetId && (options.enabled ?? true),
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      query.state.data?.setKnown === false && query.state.dataUpdateCount <= SET_NOT_KNOWN_MAX_POLLS
        ? SET_NOT_KNOWN_POLL_MS
        : false
  });
};

const saveCardOffer = async (path: string, action: CardOfferAction): Promise<CardOffer> => {
  if (action.kind === 'put') {
    return retryWhileNotSynced(() =>
      requestJson<CardOffer>({
        path,
        method: 'PUT',
        body: { physical_card_enabled: action.physical_card_enabled }
      })
    );
  }

  if (action.kind === 'delete') {
    await retryWhileNotSynced(() => requestVoid({ path, method: 'DELETE' }));
    return NO_CARD_OFFER;
  }

  throw new Error('A card offer without changes has nothing to save.');
};

/** Resolves to the offer the set has afterwards. A write right after the set
 * was created waits for Backend Sync instead of failing on its 404. */
export const useSaveCardOffer = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ benefitSetId, action }: { benefitSetId: string; action: CardOfferAction }) =>
      saveCardOffer(cardOfferPath(companyId, benefitSetId), action),
    onSuccess: (offer, { benefitSetId }) => {
      queryClient.setQueryData<CardOfferState>(cardOfferKey(companyId, benefitSetId), {
        offer,
        setKnown: true
      });
    }
  });
};
